/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Grid, Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { InputField } from '../../../../Components/FormFields/InputField';
import { DatePickerField } from '../../../../Components/FormFields/DatePickerField';
import { RadioField } from '../../../../Components/FormFields/RadioField';
import { ContractServices } from '../ContractForm/ChildComponents/ContractServices';
import { SelectField } from '../../../../Components/FormFields/SelectField';
import { Pricing } from '../../../../Components/UIElements/Pricing';
import { ContractVolumeInformation } from '../ContractForm/ChildComponents/ContractVolumeInformation';
import { checkoutSettings } from '../../settings';

/**
 * ContactForm Component
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export function ContactForm(props) {
  const { t } = useTranslation('translations');
  const {
    values: {
      rateBundle,
      rateBundleTerm,
      contractPriceAdjustmentRule,
      contractVolumeInformation,
    },
  } = useFormikContext();
  const {
    formField: {
      gender,
      firstName,
      lastName,
      dateOfBirth,
      street,
      houseNumber,
      postCode,
      city,
      email,
      phone,
      country,
    },
  } = props;

  /**
   * Render form elements
   * @returns {React.JSX.Element}
   */
  const renderFormElements = () => (
    <Grid container spacing={2}>
      <Grid item md={12}>
        <RadioField
          name={gender.name}
          label={gender.label}
          data={gender.data}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <InputField name={firstName.name} label={firstName.label} fullWidth required />
      </Grid>
      <Grid item xs={12} md={6}>
        <InputField name={lastName.name} label={lastName.label} fullWidth required />
      </Grid>
      <Grid item xs={12} md={12}>
        <DatePickerField
          sx={{
            width: '100%',
            color: 'blue',
          }}
          name={dateOfBirth.name}
          label={dateOfBirth.label}
          maxDate={dayjs(checkoutSettings.minimalDateOfBirthToCheckout)}
          disableFuture
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12} md={12} marginLeft="0.25rem">
        <Typography variant="p" sx={{ fontSize: '14px' }} mb={2} mt={2}>
          {t('dateOfBirthRestriction')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <InputField name={street.name} label={street.label} fullWidth required />
      </Grid>
      <Grid item xs={12} md={4}>
        <InputField name={houseNumber.name} label={houseNumber.label} fullWidth required />
      </Grid>
      <Grid item xs={12} md={4}>
        <InputField name={postCode.name} label={postCode.label} fullWidth required />
      </Grid>
      <Grid item xs={12} md={8}>
        <InputField name={city.name} label={city.label} fullWidth required />
      </Grid>
      <Grid item xs={12} md={12}>
        <SelectField
          name={country.name}
          label={country.label}
          data={country.data}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <InputField name={email.name} label={email.label} fullWidth required />
      </Grid>
      <Grid item xs={12} md={12}>
        <InputField name={phone.name} label={phone.label} fullWidth required />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Typography mt={4} textAlign="center" variant="h2">
        3.
        {' '}
        {t('stepThreeHeader')}
      </Typography>

      <Grid container mt={4} gap={2}>
        <Grid item border="2px solid #49ad33" xs={12} md={8} sx={{ backgroundColor: '#222' }}>
          <Box p={{
            xs: 2,
            sm: 4,
          }}
          >
            {renderFormElements()}
          </Box>
        </Grid>

        <Grid container xs item gap={2}>
          <Grid
            xs={12}
            item
            border="2px solid #49ad33"
            sx={{
              width: '100%',
              backgroundColor: '#222',
            }}
          >
            <Typography sx={{ textDecoration: 'underline' }} m={2} textAlign="center" variant="h3">
              {t('overviewOfTheRates')}
            </Typography>
            <Box p={2} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 'bold' }} textAlign="center">
                {rateBundle.name}
              </Typography>
              <Box
                mt={2}
                sx={{
                  color: '#6bb52c',
                  fontWeight: 'bold',
                }}
              >
                {`${t('duration')}: ${rateBundleTerm.termValue} ${t('months')}`}
              </Box>
              <Pricing
                price={rateBundleTerm.price}
                hasAdditionalCondition={!!contractPriceAdjustmentRule}
                sx={{ marginTop: '12px' }}
              />
              {contractPriceAdjustmentRule
                ? (
                  <Box mt={2} sx={{ fontSize: '12px' }}>
                    *
                    {' '}
                    {contractPriceAdjustmentRule.defaultDescription}
                  </Box>
                )
                : null}
              <Box>
                <ContractVolumeInformation
                  contractVolumeInformation={contractVolumeInformation?.value}
                  newContractVolumeInformation={contractVolumeInformation?.newValue}
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            xs={12}
            textAlign="center"
            item
            border="2px solid #49ad33"
            sx={{
              width: '100%',
              backgroundColor: '#222',
            }}
          >
            <Typography sx={{ textDecoration: 'underline' }} m={2} variant="h3">
              {t('services')}
            </Typography>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
            >
              <ContractServices services={rateBundle.modules} limitation={6} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

ContactForm.propTypes = {
  formField: PropTypes.object.isRequired,
};
